import { useState, useMemo, useCallback } from 'react';
import { useAdminClientePerfil, getColorHex } from '../../../hooks/useAdminClientePerfil';
import type { Prenda } from '../../../hooks/useClienteArmario';
import {
  categoriasPrenda,
  ocasionesPrenda,
  estacionesPrenda,
} from '../../../mocks/clienteArmario';

interface ArmarioProps {
  prendas: Prenda[];
  onAdd: (item: Omit<Prenda, 'id'>) => void;
  onUpdate: (id: number, updates: Partial<Prenda>) => void;
  onRemove: (id: number) => void;
  onUploadImage: (file: File) => Promise<string | null>;
  loading: boolean;
}

export default function Armario({ prendas, onAdd, onUpdate, onRemove, onUploadImage, loading }: ArmarioProps) {
  const [filter, setFilter] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('todas');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Prenda | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const filtered = useMemo(() => {
    return prendas
      .filter((p) => {
        const matchText =
          !filter ||
          p.nombre.toLowerCase().includes(filter.toLowerCase()) ||
          p.color.toLowerCase().includes(filter.toLowerCase());
        const matchCat = categoriaFilter === 'todas' || p.categoria === categoriaFilter;
        return matchText && matchCat;
      })
      .sort((a, b) => b.frecuencia_uso - a.frecuencia_uso);
  }, [prendas, filter, categoriaFilter]);

  const doUpload = useCallback(async (file: File) => {
    setUploadingImage(true);
    const url = await onUploadImage(file);
    if (url) setPreviewUrl(url);
    setUploadingImage(false);
  }, [onUploadImage]);

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await doUpload(file);
    e.currentTarget.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    await doUpload(file);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormError(null);
    setPreviewUrl(null);
    setIsDragging(false);
  };

  const handleSave = (formData: FormData) => {
    setFormError(null);
    const nombre = (formData.get('nombre') as string)?.trim();
    const categoria = (formData.get('categoria') as string)?.trim();
    const color = (formData.get('color') as string)?.trim();
    const talla = (formData.get('talla') as string)?.trim() || null;
    const material = (formData.get('material') as string)?.trim() || null;
    const imagen_url = previewUrl ?? (editing?.imagen_url || null);
    const notas = (formData.get('notas') as string)?.trim() || null;
    const frecuencia = parseInt((formData.get('frecuencia_uso') as string) || '0', 10);

    const ocasionRaw = (formData.get('ocasion') as string)?.trim() || '';
    const ocasion = ocasionRaw ? ocasionRaw.split(',').map((s) => s.trim()).filter(Boolean) : [];

    const estacionRaw = (formData.get('estacion') as string)?.trim() || '';
    const estacion = estacionRaw ? estacionRaw.split(',').map((s) => s.trim()).filter(Boolean) : [];

    if (!nombre || !categoria || !color) {
      setFormError('Nombre, categoría y color son obligatorios.');
      return;
    }

    const payload: Omit<Prenda, 'id'> = {
      nombre,
      categoria,
      color,
      ocasion,
      imagen_url,
      frecuencia_uso: isNaN(frecuencia) ? 0 : frecuencia,
      estacion,
      talla,
      material,
      notas,
      activa: true,
      sort_order: editing?.sort_order ?? prendas.length,
    };

    if (editing) {
      onUpdate(editing.id, payload);
      setEditing(null);
    } else {
      onAdd(payload);
      setShowForm(false);
    }
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-brand-black text-3xl font-light">Mi Armario</h1>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            {prendas.length} prendas registradas
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditing(null);
            setFormError(null);
          }}
          className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
        >
          + Nueva prenda
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Buscar prenda..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
        />
        <select
          value={categoriaFilter}
          onChange={(e) => setCategoriaFilter(e.target.value)}
          className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
        >
          <option value="todas">Todas las categorías</option>
          {categoriasPrenda.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Formulario */}
      {(showForm || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave(new FormData(e.currentTarget));
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Prenda' : 'Nueva Prenda'}
            </h3>
            <button
              type="button"
              onClick={resetForm}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5" />
              <p className="font-sans text-red-600 text-sm">{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Nombre *
              </label>
              <input
                name="nombre"
                defaultValue={editing?.nombre ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Categoría *
              </label>
              <select
                name="categoria"
                defaultValue={editing?.categoria ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white"
              >
                <option value="">Seleccionar...</option>
                {categoriasPrenda.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Color *
              </label>
              <input
                name="color"
                defaultValue={editing?.color ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Talla
              </label>
              <input
                name="talla"
                defaultValue={editing?.talla ?? ''}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Material
              </label>
              <input
                name="material"
                defaultValue={editing?.material ?? ''}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Usos registrados
              </label>
              <input
                name="frecuencia_uso"
                type="number"
                min={0}
                defaultValue={editing?.frecuencia_uso ?? 0}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Ocasiones (separadas por coma)
              </label>
              <select
                name="ocasion"
                defaultValue={editing?.ocasion?.join(', ') ?? ''}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white"
              >
                <option value="">Seleccionar...</option>
                {ocasionesPrenda.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Estaciones (separadas por coma)
              </label>
              <select
                name="estacion"
                defaultValue={editing?.estacion?.join(', ') ?? ''}
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white"
              >
                <option value="">Seleccionar...</option>
                {estacionesPrenda.map((e) => (
                  <option key={e.value} value={e.value}>
                    {e.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Upload directo de imagen con drag & drop + cámara móvil */}
          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Foto de la prenda
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`mt-2 rounded-xl border-2 border-dashed p-6 flex flex-col items-center justify-center gap-3 transition-colors ${
                isDragging
                  ? 'border-brand-black bg-brand-sage/10'
                  : 'border-brand-gray-200 bg-brand-gray-50 hover:border-brand-gray-400'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                isDragging ? 'bg-brand-sage/20 text-brand-black' : 'bg-brand-gray-100 text-brand-gray-400'
              }`}>
                <i className="ri-camera-line text-2xl" />
              </div>
              <div className="text-center">
                <p className="font-sans text-brand-black text-sm font-medium">
                  {uploadingImage ? 'Subiendo...' : isDragging ? 'Suelta la foto aquí' : 'Arrastra una foto, selecciona archivo o usa la cámara'}
                </p>
                <p className="font-sans text-brand-gray-400 text-xs mt-1">JPG, PNG, WEBP · máx. 5 MB</p>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <label className="inline-flex items-center gap-2 bg-white border border-brand-gray-200 hover:border-brand-black transition-colors rounded-lg px-4 py-2 font-sans text-sm text-brand-gray-700 cursor-pointer">
                  <i className="ri-image-line" />
                  Galería
                  <input type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />
                </label>
                <label className="inline-flex items-center gap-2 bg-white border border-brand-gray-200 hover:border-brand-black transition-colors rounded-lg px-4 py-2 font-sans text-sm text-brand-gray-700 cursor-pointer">
                  <i className="ri-camera-fill" />
                  Cámara
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileInputChange} />
                </label>
              </div>
              {previewUrl && (
                <span className="font-sans text-brand-sage text-xs">Imagen lista para guardar</span>
              )}
              {editing?.imagen_url && !previewUrl && (
                <span className="font-sans text-brand-gray-400 text-xs">Manteniendo imagen actual</span>
              )}
            </div>
          </div>

          {/* Preview de imagen */}
          {(previewUrl || editing?.imagen_url) && (
            <div className="border border-brand-gray-200 rounded-xl p-3 w-fit">
              <p className="font-sans text-brand-gray-500 text-xs mb-2">Vista previa</p>
              <img
                src={previewUrl || editing?.imagen_url || ''}
                alt={editing?.nombre || 'Prenda'}
                className="w-32 h-40 object-cover rounded-lg"
              />
            </div>
          )}

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Notas
            </label>
            <textarea
              name="notas"
              defaultValue={editing?.notas ?? ''}
              rows={3}
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={loading || uploadingImage}
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-60"
            >
              {loading || uploadingImage ? 'Guardando...' : 'Guardar Prenda'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Grid de prendas */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 text-brand-gray-500 text-sm py-20">
          <i className="ri-loader-4-line animate-spin" />
          <span>Cargando armario...</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((prenda) => {
            const colorHex = getColorHex(prenda.color);
            return (
              <div
                key={prenda.id}
                className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden hover:border-brand-gray-400 transition-colors"
              >
                <div className="aspect-[3/4] bg-brand-gray-50 relative">
                  {prenda.imagen_url ? (
                    <img
                      src={prenda.imagen_url}
                      alt={prenda.nombre}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-brand-gray-400">
                      <div className="w-12 h-12 flex items-center justify-center">
                        <i className="ri-image-line text-2xl" />
                      </div>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button
                      onClick={() => {
                        setEditing(prenda);
                        setShowForm(false);
                        setFormError(null);
                        setPreviewUrl(null);
                      }}
                      className="w-7 h-7 rounded-lg bg-white/90 backdrop-blur flex items-center justify-center text-brand-gray-600 hover:text-brand-black"
                      title="Editar"
                    >
                      <i className="ri-pencil-line text-xs" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`¿Eliminar "${prenda.nombre}"?`)) {
                          onRemove(prenda.id);
                        }
                      }}
                      className="w-7 h-7 rounded-lg bg-white/90 backdrop-blur flex items-center justify-center text-brand-gray-600 hover:text-red-600"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line text-xs" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <p className="font-sans text-brand-black text-sm font-medium line-clamp-1">
                    {prenda.nombre}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span
                      className="w-2.5 h-2.5 rounded-full border border-brand-gray-300/50 shrink-0"
                      style={{ backgroundColor: colorHex }}
                      title={prenda.color}
                    />
                    <p className="font-sans text-brand-gray-500 text-xs">
                      {prenda.categoria} · {prenda.color}
                    </p>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-sans text-brand-sage text-xs font-medium">
                      {prenda.frecuencia_uso} usos
                    </span>
                    <span className="font-sans text-brand-gray-400 text-xs">
                      {prenda.talla}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-400 mb-4">
            <i className="ri-t-shirt-line text-2xl" />
          </div>
          <p className="font-sans text-brand-gray-500 text-sm">
            No se encontraron prendas. Agrega tu primera prenda o ajusta los filtros.
          </p>
        </div>
      )}
    </div>
  );
}
